import { mutate } from 'swr'
import Cache from '@fs/zion-cache'
import axios from '@fs/zion-axios'
import type {
  AssociationConclusionType,
  AssociationContribution,
  AssociationEventConclusion,
  AssociationType,
  PersonAssociation,
  PersonId,
  PID,
  RelationshipId,
  RelationshipType,
  SummaryPerson,
  TreeId,
} from '@fs/zion-tree-types'
import { useGroupsContext } from '@fs/zion-tree-groups'
import type { SwrResponse } from './useSwrRequest'
import { updateRequestMultiple, useSwrRequest } from './useSwrRequest'
import { swrKey as changeLogKey } from './useChangeLogSummary'

const cache = new Cache({
  storeName: 'association_relationship_all_r9',
  encrypted: process.env.NODE_ENV === 'production',
})

const swrKey = (pid: PersonId, treeId: string): string =>
  `/service/tree/tree-data/v8/person/${pid}/associations?treeId=${treeId}`
const urlBase = '/service/tree/tree-data/relationship/association'
function keysToUpDate(pid: PersonId, treeId: string): string[] {
  return [swrKey(pid, treeId), changeLogKey(pid, treeId)]
}

/**
 * Gets all "other relationships" for a person.
 */
export function useOtherRelationships(
  pid?: PersonId
): SwrResponse<PersonAssociation[]> & { relationships?: PersonAssociation[] } {
  const { currentTreeId } = useGroupsContext()
  const response = useSwrRequest<PersonAssociation[]>(pid ? swrKey(pid, currentTreeId || 'PRIVATE') : null, cache)
  return { ...response, relationships: response.data }
}

export type OneOtherRelationshipResponse = {
  associationType: AssociationType
  contributions: {
    person1Contribution: AssociationContribution
    person2Contribution: AssociationContribution
  }
  deleted: boolean
  facts: AssociationEventConclusion[]
  hasSources: boolean
  id: RelationshipId
  person1: SummaryPerson & { id: PID }
  person2: SummaryPerson & { id: PID }
  person1Id: PID
  person2Id: PID
  readOnly: boolean
}

/**
 * Gets one "other relationship" for a person.
 */
export function useOneOtherRelationship(
  relationshipId: RelationshipId | undefined,
  relationshipType: RelationshipType | undefined
): SwrResponse<OneOtherRelationshipResponse> {
  const swrRelationshipKey = relationshipType === 'association' ? `${urlBase}/${relationshipId}` : null
  return useSwrRequest<OneOtherRelationshipResponse>(swrRelationshipKey)
}

export async function mutateOtherRelationships(pid: PersonId, treeId: string): Promise<void> {
  mutate(swrKey(pid, treeId))
}

export async function addOtherRelationship(
  pid: PersonId,
  treeId: string,
  associationType: AssociationType,
  person1Id: PersonId,
  person2Id: PersonId,
  reason = ''
): Promise<void> {
  const url = `${urlBase}?associationType=${associationType}&person1Id=${person1Id}&person2Id=${person2Id}`

  await updateRequestMultiple(keysToUpDate(pid, treeId), () => axios.post(url, { reason }))
}

export async function deleteOtherRelationship(
  pid: PersonId,
  treeId: TreeId | 'PRIVATE',
  associationId: RelationshipId,
  reason = ''
): Promise<void> {
  const url = `${urlBase}/${associationId}`

  await updateRequestMultiple(keysToUpDate(pid, treeId), () => axios.delete(url, { data: { reason } }))
}

export async function switchOtherRelationshipRoles(
  focusPersonId: PersonId,
  treeId: string,
  newPerson1Id: PersonId,
  newPerson2Id: PersonId,
  associationId: string,
  reason = ''
): Promise<void> {
  const url = `${urlBase}/${associationId}/persons/order`
  await updateRequestMultiple(keysToUpDate(focusPersonId, treeId), () =>
    axios.put(url, { changeMessage: reason, person1Id: newPerson1Id, person2Id: newPerson2Id })
  )
}

export async function updateOtherRelationship(
  person: 'person1' | 'person2',
  pid: PersonId,
  treeId: string,
  associationId: string,
  newPersonId: PersonId,
  reason = ''
): Promise<void> {
  const url =
    person === 'person1'
      ? `${urlBase}/${associationId}/update/person1?newPerson1Id=${newPersonId}`
      : `${urlBase}/${associationId}/update/person2?newPerson2Id=${newPersonId}`
  await updateRequestMultiple(keysToUpDate(pid, treeId), () => axios.put(url, { reason }))
}

export const associationTypeToConclusionType = {
  MASTER_TO_APPRENTICE: 'APPRENTICESHIP',
  EMPLOYER_TO_EMPLOYEE: 'EMPLOYMENT',
  SLAVEHOLDER_TO_ENSLAVED_PERSON: 'SLAVERY',
  GODPARENT_TO_GODCHILD: 'GODPARENTHOOD',
  HEAD_OF_HOUSEHOLD_TO_OCCUPANT: 'HOUSEHOLD',
  NEIGHBOR_TO_NEIGHBOR: 'NEIGHBORHOOD',
  RELATIVE_TO_RELATIVE: 'RELATION',
} satisfies Record<Exclude<AssociationType, 'ANCESTOR_TO_DESCENDANT'>, Exclude<AssociationConclusionType, 'GENERATION'>>
